import {
  addDays,
  addHours,
  differenceInHours,
  endOfHour,
  format,
  isBefore,
  startOfDay,
  startOfHour,
  startOfToday,
} from 'date-fns';
import { ar } from 'date-fns/locale';
import { Clock } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@cold-ambulance/shadcn';

export function generateHours(
  startDate: string | Date,
  endDate: string | Date,
  includeEnd: boolean = false,
): HourOption[] {
  let startHour = startOfHour(startDate);
  let endHour = endOfHour(endDate);

  // If the range is inverted, swap it. Equality is allowed.
  if (isBefore(endHour, startHour)) {
    const tmpStart = startHour;
    startHour = startOfHour(endDate);
    endHour = endOfHour(tmpStart);
  }

  const count = Math.max(
    0,
    differenceInHours(endHour, startHour) + (includeEnd ? 1 : 0),
  );

  const options: HourOption[] = Array.from({ length: count }, (_, i) => {
    const dt = addHours(startHour, i);
    return {
      label: format(dt, 'hh:00 a', { locale: ar }),
      value: dt.toISOString(),
      hour24: dt.getHours(),
      date: dt,
    };
  });

  return options;
}

function HourField({
  label,
  startAt,
  endAt,
  onChange,
  value,
  includeEnd = false,
}: {
  label: string;
  startAt?: string | Date;
  endAt?: string | Date;
  value?: string;
  includeEnd?: boolean;
  onChange: (value: string) => void;
}) {
  const endBoundary =
    endAt ?? (startAt ? addDays(startOfDay(new Date(startAt)), 1) : undefined);
  const dayHours =
    startAt && endBoundary
      ? generateHours(startAt, endBoundary, includeEnd)
      : [];

  return (
    <div className="flex w-full flex-col gap-4">
      <Label>{label}</Label>
      <Select dir="rtl" onValueChange={onChange} value={value}>
        <SelectTrigger className="min-w-full" icon={Clock}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {dayHours.map((hour) => (
            <SelectItem key={hour.value} value={hour.value}>
              {hour.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export type HourOption = {
  label: string;
  value: string;
  hour24: number;
  date: Date;
};

export function HoursField(props: {
  className?: string;
  value?: { from: string; to: string };
  onChange: (value: Required<{ from: string; to: string }>) => void;
}) {
  const { t } = useTranslation();
  const { className, value, onChange } = props;
  const [otherDate, setOtherDate] = useState(value?.from);

  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      <div className="flex items-center gap-3">
        <HourField
          label={t('newRequest.fromHour')}
          startAt={startOfToday()}
          endAt={addDays(startOfToday(), 1)}
          value={value?.from}
          onChange={(selected) => {
            setOtherDate(addHours(new Date(selected), 1).toISOString());
            if (value?.to && isBefore(selected, value?.to)) {
              onChange({ from: selected, to: value.to });
            } else {
              onChange({
                from: selected,
                to: '',
              });
            }
          }}
        />
        <HourField
          label={t('newRequest.toHour')}
          startAt={otherDate}
          endAt={
            value?.from
              ? addDays(startOfDay(value.from), 1)
              : addDays(startOfToday(), 1)
          }
          value={value?.to}
          includeEnd={true}
          onChange={(selected) => {
            onChange({ from: value?.from!, to: selected });
          }}
        />
      </div>
    </div>
  );
}
